// src/services/otherCRMService.js
import { fetchWithAuth } from '../utils/fetchWithAuth';

/* ========= helpers ========= */
const parseJSONSafe = async (res) => {
  const text = await res.text();
  if (!text) return {};
  try { return JSON.parse(text); } catch { return {}; }
};

const mustOk = async (res) => {
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return parseJSONSafe(res);
};

const compact = (obj) =>
  Object.fromEntries(
    Object.entries(obj || {}).filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        !(Array.isArray(v) && v.length === 0) &&
        String(v) !== ''
    )
  );

const qs = (params) => {
  if (!params) return '';
  const s = new URLSearchParams(compact(params)).toString();
  return s ? `?${s}` : '';
};

const normalizeMinute = (m = {}) => {
  const meetingId =
    m.meeting?.id ?? m.meetingId ?? m.meeting_id ?? null;
  const viewUrl =
    m.viewUrl ??
    m.fileSecureUrl ??
    m.secureUrl ??
    m.url ??
    m.fileUrl ??
    m.path ??
    null;
  const downloadUrl =
    m.downloadUrl ??
    m.fileSecureUrl ??
    m.secureUrl ??
    m.url ??
    m.fileUrl ??
    m.path ??
    null;
  const title = m.title ?? m.name ?? m.filename ?? m.originalName ?? null;
  const date = m.date ?? m.created_at ?? m.createdAt ?? null;
  return { ...m, meetingId, viewUrl, downloadUrl, title, date };
};

const unwrap = (x) => x?.data?.data ?? x?.data ?? x?.item ?? x?.minute ?? x;

/* ========= ENTITIES ========= */
// POST /others-crm/entity
export const otherCrmCreateEntity = async (payload) => {
  const body = JSON.stringify(compact(payload));
  const res = await fetchWithAuth(`/others-crm/entity`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  });
  return mustOk(res);
};

// PUT /others-crm/entity/:id
export const otherCrmUpdateEntity = async (id, payload) => {
  const body = JSON.stringify(compact(payload));
  const res = await fetchWithAuth(`/others-crm/entity/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body,
  });
  return mustOk(res);
};

// DELETE /others-crm/entity/:id
export const otherCrmDeleteEntity = async (id) => {
  const res = await fetchWithAuth(`/others-crm/entity/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return mustOk(res);
};

/* ========= SUMMARY & ANALYTICS ========= */
// GET /others-crm/summary?year=&is_public=&startDate=&endDate=
export const otherCrmListSummary = async (params) => {
  const res = await fetchWithAuth(`/others-crm/summary${qs(params)}`, { method: 'GET' });
  const data = await mustOk(res);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

// GET /others-crm/analytics?year=&excludeId=&is_public=&startDate=&endDate=
export const otherCrmAnalytics = async (params) => {
  const res = await fetchWithAuth(`/others-crm/analytics${qs(params)}`, { method: 'GET' });
  return mustOk(res);
};

/* ========= DETAIL (por ID) ========= */
// GET /others-crm/entity/:id
export const otherCrmCompanyDetail = async (entityId, params) => {
  if (!entityId && entityId !== 0) {
    return { company: null, meetings: [], minutes: [], notes: [] };
  }
  const id = encodeURIComponent(entityId);
  const res = await fetchWithAuth(`/others-crm/entity/${id}${qs(params)}`, { method: 'GET' });
  const out = await mustOk(res);

  const minutesRaw = Array.isArray(out?.minutes) ? out.minutes : [];
  return {
    company: out?.company ?? null,
    meetings: Array.isArray(out?.meetings) ? out.meetings : [],
    minutes: minutesRaw.map(normalizeMinute),
    notes: Array.isArray(out?.notes) ? out.notes : [],
    counters: out?.counters,
  };
};

/* ========= MEETINGS ========= */
// POST /others-crm/entity/:id/meetings
export const otherCrmCreateMeeting = async ({
  entityId,
  date, note, title, description, category, location, participants, tags,
}) => {
  if (!entityId && entityId !== 0) {
    throw new Error('Falta entityId para crear reunión');
  }
  const body = JSON.stringify(
    compact({ date, note, title, description, category, location, participants, tags })
  );
  const res = await fetchWithAuth(
    `/others-crm/entity/${encodeURIComponent(entityId)}/meetings`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body }
  );
  return mustOk(res);
};

// PUT /others-crm/meetings/:id
export const otherCrmUpdateMeeting = async (id, payload) => {
  const body = JSON.stringify(compact(payload));
  const res = await fetchWithAuth(`/others-crm/meetings/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body,
  });
  return mustOk(res);
};

// DELETE /others-crm/meetings/:id
export const otherCrmDeleteMeeting = async (id) => {
  const res = await fetchWithAuth(`/others-crm/meetings/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return mustOk(res);
};

/* ========= MINUTES ========= */
// POST /others-crm/entity/:id/minutes/upload
export const otherCrmUploadMinute = async ({
  entityId, title, date, file, meetingId, participants, tags,
}) => {
  if (!entityId && entityId !== 0) {
    throw new Error('Falta entityId para subir minuta');
  }
  const fd = new FormData();
  if (title) fd.append('title', title);
  if (date)  fd.append('date', date);
  if (meetingId) fd.append('meetingId', String(meetingId));
  if (participants) fd.append('participants', JSON.stringify(participants));
  if (tags) fd.append('tags', JSON.stringify(tags));
  fd.append('file', file);

  const res = await fetchWithAuth(
    `/others-crm/entity/${encodeURIComponent(entityId)}/minutes/upload`,
    { method: 'POST', body: fd } // no seteamos Content-Type
  );
  const raw = await mustOk(res);
  const minute = normalizeMinute(unwrap(raw));
  return Object.keys(minute).length ? minute : normalizeMinute({ meetingId, title, date });
};

// DELETE /others-crm/minutes/:id
export const otherCrmDeleteMinute = async (id) => {
  const res = await fetchWithAuth(`/others-crm/minutes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return mustOk(res);
};

/* ========= RECENT MEETINGS ========= */
// GET /others-crm/recent-meetings?limit=
export const otherCrmGetRecentMeetings = async (limit = 3) => {
  try {
    const res = await fetchWithAuth(`/others-crm/recent-meetings?limit=${limit}`, { method: 'GET' });
    const data = await mustOk(res);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.meetings)) return data.meetings;
    return [];
  } catch {
    return [];
  }
};

/* ========= NOTES (por ID) ========= */
// GET /others-crm/entity/:id/notes
export const otherCrmListNotes = async (entityId, params) => {
  if (!entityId && entityId !== 0) {
    throw new Error('Falta entityId para listar notas');
  }
  const res = await fetchWithAuth(
    `/others-crm/entity/${encodeURIComponent(entityId)}/notes${qs(params)}`,
    { method: 'GET' }
  );
  return mustOk(res);
};

// POST /others-crm/entity/:id/notes
export const otherCrmCreateNote = async ({ entityId, date, title, body }) => {
  if (!entityId && entityId !== 0) {
    throw new Error('Falta entityId para crear nota');
  }
  const json = JSON.stringify(compact({ date, title, body }));
  const res = await fetchWithAuth(
    `/others-crm/entity/${encodeURIComponent(entityId)}/notes`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: json }
  );
  return mustOk(res);
};

// PUT /others-crm/notes/:id
export const otherCrmUpdateNote = async (id, payload) => {
  const json = JSON.stringify(compact(payload));
  const res = await fetchWithAuth(`/others-crm/notes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: json,
  });
  return mustOk(res);
};

// DELETE /others-crm/notes/:id
export const otherCrmDeleteNote = async (id) => {
  const res = await fetchWithAuth(`/others-crm/notes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return mustOk(res);
};

/* ========= LOGO UPLOAD ========= */
// POST /others-crm/entity/upload-logo
export const otherCrmUploadLogo = async (file) => {
  if (!file) throw new Error('Archivo requerido');
  const fd = new FormData();
  fd.append('logo', file);
  const res = await fetchWithAuth('/others-crm/entity/upload-logo', {
    method: 'POST',
    body: fd,
  });
  return mustOk(res);
};

// DELETE /others-crm/entity/delete-logo
export const otherCrmDeleteLogo = async (imageUrl) => {
  const res = await fetchWithAuth('/others-crm/entity/delete-logo', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageUrl }),
  });
  return mustOk(res);
};
